<template>


    <div v-if="usePaging" class="paging-controls">


        <button v-if="prevIndex === -1" class="btn btn-outline-primary disabled" disabled>
            {{ btnPreviousText }}
        </button>
        <a v-else
           rel="prev"
           @click.prevent="doPage(prevIndex)"
           v-bind:href="createUrlForPage(prevIndex)"
           class="btn btn-outline-primary">{{ btnPreviousText }}</a>
        <span class="page-buttons-wrapper">

            <a 
               v-bind:href="firstPageEntry.url"
               @click.prevent="doPage(firstPageEntry.page)"
               :class="renderCssClass(firstPageEntry)">{{renderPageNumber(firstPageEntry)}}</a>

            <span class="-with3 elipsis" v-if="hasGapBeforeWith3">...</span>

            <a 
               v-bind:href="pd.url" 
               @click.prevent="doPage(pd.page)"
               :class="renderCssClass(pd)" 
               v-for="pd in pagesArray" 
               :key="pd.page">{{renderPageNumber(pd)}}</a>

            <span class="-with3 elipsis" v-if="hasGapAfterWith3">...</span>

            <a 
               v-bind:href="lastPageEntry.url" 
               @click.prevent="doPage(lastPageEntry.page)"
               :class="renderCssClass(lastPageEntry)">{{renderPageNumber(lastPageEntry)}}</a>

        </span>

        <button v-if="nextIndex === -1"
                class="btn btn-outline-primary disabled"
                disabled>
            {{ btnNextText }}
        </button>
        <a v-else
           rel="next"
           v-bind:href="createUrlForPage(nextIndex)"
           @click.prevent="doPage(nextIndex)"
           class="btn btn-outline-primary">{{ btnNextText }}</a>
    </div>




</template>



<style scoped src="./paging_control_styles.css"></style>

<script lang="ts">


    import { defineComponent, onMounted, ref, PropType, watch, reactive } from 'vue';
    import PagingModel from '@Apps/Search/Src/Models/PagingModel';
    import { FilterModel } from '@Apps/Search/Src/Models/FilterModel';
    import { toUrl } from '@Apps/Search/Src/Logic/urlConvert';
    import appTranslations from "@Apps/Search/Translations/VueTranslations"

    interface PageData {

        page: number;
        url: string;
        showFor3Design: boolean;
    }

    export default defineComponent({

        props: {

            paging: {
                type: Object as PropType<PagingModel>,
                required: true
            },

            filter: {
                type: Object as PropType<FilterModel>,
                required: true
            }
        },

        emits: ["onPage"],

        setup(props, context) {

            const usePaging = ref(false);

            const prevIndex = ref(0);
            const nextIndex = ref(0);

            const btnPreviousText = appTranslations.searchApp_Pagination_Back;
            const btnNextText = appTranslations.searchApp_Pagination_Next;

            const createUrlForPage = (page: number) => {

                return '?' + toUrl(props.filter!, props.paging!.clone(page));
            };

            const renderPageNumber = (data: PageData) => { return data.page + 1; };

            const renderCssClass = (data: PageData) => {


                return ((data.page == props.paging?.page) ? "active" : "") + " " + "btn btn-small-number" + " " + (data.showFor3Design ? "-with3" : " ");

            };



            const pagesArray = ref<PageData[]>([]);

            let firstPageEntry = reactive<PageData>({ page: 0, url: "", showFor3Design: true });
            let lastPageEntry = reactive<PageData>({ page: -1, url: "", showFor3Design: true });

            // note that we currently have a design with max of 3 items
            // if we want to have a different version, we need to calculate the has gap
            // for that scenario as well because we wont be able to  do that in css (dynamically determine where the ... should be shown)
            let hasGapBeforeWith3 = ref(false);
            let hasGapAfterWith3 = ref(false);


            const setData = () => {

                usePaging.value = props.paging.hasPaging();
                prevIndex.value = props.paging.getPrevious();
                nextIndex.value = props.paging.getNext();

                pagesArray.value.length = 0;

                if (usePaging.value) {

                    const lastPage = (props.paging.pageCount ?? 0) - 1;


                    firstPageEntry.url = createUrlForPage(0);

                    lastPageEntry.page = lastPage;
                    lastPageEntry.url = createUrlForPage(lastPage);


                    // add any pages in between
                    // do not add first or last page
                    // we will try and get a few before and after

                    var active = props.paging.page ?? -1;


                    // this determines if we want to show elipsis between the active item and the first and last item
                    hasGapBeforeWith3.value = active > 1;
                    hasGapAfterWith3.value = (lastPage - active) > 1;

                    // for the with 3 design, we only want to show the first entry before the active entry:
                    // if the active entry is the last entry

                    // for the with 3 design we only want to show the first entry after the active entry:
                    // if the active entry is the first entry..


                    // render items *before* the current active entry (if there are entries before, and limit to 3)
                    let counter = 0;
                    for (let i = active - 1; i > 0; i--) {

                        pagesArray.value.unshift({ page: i, url: createUrlForPage(i), showFor3Design: lastPageEntry.page == active && counter === 0 });

                        // take at most 3:
                        if (counter >= 3) break;
                        counter++;
                    }

                    // render active here if not first or last
                    if (active !== 0 && active !== lastPage) {
                        pagesArray.value.push({ page: active, url: createUrlForPage(active), showFor3Design: true });
                    }

                    // render items *after* the current active entry (if there are entries after, and limit to 3)
                    counter = 0;
                    for (let i = active + 1; i < lastPage; i++) {

                        pagesArray.value.push({ page: i, url: createUrlForPage(i), showFor3Design: firstPageEntry.page == active && counter === 0 });
                        // take at most 3:
                        if (counter >= 3) break;
                        counter++;
                    }


                }
                else {
                    // could clear, but does not really matter since there is a v-if on the same property..
                }

            };

            setData();

            watch(props.filter, () => {

                setData();


            },
                {
                    deep: true,
                    flush: "post"
                }
            )

            watch(props.paging, () => {

                setData();


            },
                {
                    deep: true,
                    flush: "post"
                }
            )



            const doPage = (page: number) => {

                context.emit("onPage", page);
            };





            return {
                usePaging,
                prevIndex,
                nextIndex,
                createUrlForPage,
                firstPageEntry,
                hasGapBeforeWith3,
                pagesArray,
                hasGapAfterWith3,
                lastPageEntry,
                renderPageNumber,
                renderCssClass,
                doPage,

                btnPreviousText,
                btnNextText
            };
        }

    });

</script>